import * as THREE from "three"

import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader"

import { sceneManager } from "../index"
import { scene, orbitControl, camera, options } from "../SceneManager"
import { quickSave } from "../helpers/SaveManager"
import Joint, { jointColors } from "./Joint"
import ModelExporter from "../ModelExporter"
import Subject from "./Subject"
import { addCharacterToTabs, removeFromTabs } from "../helpers/Tabs"
import { animateTransition } from "../helpers/Helpers"
import { showConfirm, showMessage } from "../Message"
import { t } from "../helpers/Translate"

class Character extends Subject {
	constructor({id, info, position, scale, pose, color, jointScales}) {
		super()
		this.subjectType = "Character"
		this.id = id || Math.floor(Math.random() * Math.floor(9999))
		this.info = info
		this.title = this.info.title
		this.thumbnail = this.info.thumbnail
		this.object = null
		this.alive = true
		this.jointsHidden = false
		this.color = color || this.info.color

		this.loading = true

		this.joints = []
		const modelLoader = new FBXLoader()
		this.startLoading(position)

		let loadTimeoutId = 0

		modelLoader.load(
			this.info.path,
			(rig) => {
				this.completeLoading()
				this.object = rig
				this.traverseJoints((child) => {
					const jointInfo = this.info.joints.find(
						(joint) => joint.name === child.name
					)
					const jointName = jointInfo.name
					const jointScale = this.info.jointSize
						? jointInfo.scale / this.info.jointSize
						: jointInfo.scale

					const jointColor = jointInfo.color

					const joint = new Joint(
						jointScale,
						jointColor,
						jointName,
						this.info.scale,
						jointInfo.type,
						jointInfo.axis
					)

					this.joints.push(joint)

					child.add(joint)
					joint.resetScale()

					if (joint.name.indexOf("HandMiddle1") > 0) {
						const side = joint.name.indexOf("Left") > 0 ? "Left" : "Right"
						const handSelector = new Joint(1, 0xff00d0, side, 1, "selector")
						this.joints.push(handSelector)
						child.add(handSelector)
					}

					if (joint.name == "mixamorigHips") {
						this.hip = child
					}
				})

				this.setMaterial()

				scene.add(this.object)

				sceneManager.setGizmo(sceneManager.activeGizmo)

				this.setPose(
					pose ||
						(this.info.defaultPose != null &&
							JSON.parse(
								'{"mixamorigHips":{"x":0,"y":0,"z":0},"mixamorigSpine":{"x":0,"y":0,"z":0},"mixamorigSpine1":{"x":0,"y":0,"z":0},"mixamorigSpine2":{"x":0,"y":0,"z":0},"mixamorigLeftShoulder":{"x":0.002762085286541827,"y":-0.02333164018963318,"z":-0.30644480920182166},"mixamorigLeftArm":{"x":0.09829070287134517,"y":-0.06125606378546322,"z":-1.04602694412666},"mixamorigLeftForeArm":{"x":0,"y":-0.3651249345124468,"z":0},"mixamorigLeftHand":{"x":0,"y":0,"z":0.09441514290351417},"mixamorigLeftHandThumb1":{"x":0.3048936435682473,"y":-0.6356577151160914,"z":-0.3427786526930735},"mixamorigLeftHandThumb2":{"x":0,"y":0.253106164325205,"z":0},"mixamorigLeftHandThumb3":{"x":0,"y":0.3730901827301244,"z":0},"mixamorigLeftHandIndex1":{"x":0.009659063544175407,"y":-0.11736259307637147,"z":-0.11824993176526573},"mixamorigLeftHandIndex2":{"x":0,"y":0,"z":-0.2801542826486143},"mixamorigLeftHandIndex3":{"x":0,"y":0,"z":-0.2722167428903134},"mixamorigLeftHandMiddle1":{"x":-0.28548722505781743,"y":0.08072916657622693,"z":-0.26813720750330766},"mixamorigLeftHandMiddle2":{"x":0,"y":0,"z":-0.17954080306549886},"mixamorigLeftHandMiddle3":{"x":0,"y":0,"z":0},"mixamorigLeftHandRing1":{"x":-3.499509873453152e-18,"y":0.1311711641649631,"z":-0.07585207164537583},"mixamorigLeftHandRing2":{"x":0,"y":0,"z":-0.45873317092381716},"mixamorigLeftHandRing3":{"x":0,"y":0,"z":-0.3417225994923424},"mixamorigLeftHandPinky1":{"x":0,"y":0.243260505067174,"z":-0.21751632394657616},"mixamorigLeftHandPinky2":{"x":0,"y":0,"z":-0.6341538059946643},"mixamorigLeftHandPinky3":{"x":0,"y":0,"z":0},"mixamorigNeck":{"x":0,"y":0,"z":0},"mixamorigHead":{"x":0,"y":0,"z":0},"mixamorigRightShoulder":{"x":0.006621998233596858,"y":0.02199390232103722,"z":0.28197737060283967},"mixamorigRightArm":{"x":0.06208091224964726,"y":0.14827341917068665,"z":1.0127620434089022},"mixamorigRightForeArm":{"x":0.010155675540900544,"y":0.25846208103003515,"z":0.03820393686587353},"mixamorigRightHand":{"x":0,"y":0,"z":0},"mixamorigRightHandIndex1":{"x":0,"y":0,"z":0.12453104812793421},"mixamorigRightHandIndex2":{"x":0,"y":0,"z":0.5271800886311687},"mixamorigRightHandIndex3":{"x":0.03100821907657763,"y":0.021098397359143366,"z":0.3327001822181205},"mixamorigRightHandPinky1":{"x":0,"y":0,"z":0.514319563301136},"mixamorigRightHandPinky2":{"x":0,"y":0,"z":0.3731282404648059},"mixamorigRightHandPinky3":{"x":0,"y":0,"z":0.26693403757346806},"mixamorigRightHandMiddle1":{"x":0,"y":0,"z":0.2661530691689823},"mixamorigRightHandMiddle2":{"x":0.10716766540182329,"y":0.05179545602380055,"z":0.4635536152799572},"mixamorigRightHandMiddle3":{"x":0,"y":0,"z":0.42336989690034604},"mixamorigRightHandThumb1":{"x":0,"y":0.5289124119045862,"z":0.25056996557945405},"mixamorigRightHandThumb2":{"x":0,"y":-0.2461153087712199,"z":0},"mixamorigRightHandThumb3":{"x":0,"y":-0.1489437325040402,"z":0},"mixamorigRightHandRing1":{"x":0,"y":0,"z":0.40850872525429294},"mixamorigRightHandRing2":{"x":0.0316595657208515,"y":0.01310170078247582,"z":0.4174782357887487},"mixamorigRightHandRing3":{"x":0.11998929075426844,"y":-0.03768309981557704,"z":0.3118338799311934},"mixamorigRightUpLeg":{"x":0,"y":0,"z":0},"mixamorigRightLeg":{"x":0,"y":0,"z":0},"mixamorigRightFoot":{"x":0,"y":0,"z":0},"mixamorigRightToeBase":{"x":0,"y":0,"z":0},"mixamorigLeftUpLeg":{"x":0,"y":0,"z":0},"mixamorigLeftLeg":{"x":0,"y":0,"z":0},"mixamorigLeftFoot":{"x":0,"y":0,"z":0},"mixamorigLeftToeBase":{"x":0,"y":0,"z":0}}'
							))
					, false
				)

				this.setScale(scale)
				this.setJointScales(jointScales)
				this.setPosition(position, false)

				addCharacterToTabs(this)
				sceneManager.selectedManager.select(this)

				this.loading = false
				clearTimeout(loadTimeoutId)
			},
			(xhr) => {
				if (xhr.lengthComputable) {
					clearTimeout(loadTimeoutId)
					loadTimeoutId = setTimeout(() => {
						showMessage(`${t('Failed to load')} ${this.title}`)
						this.loading = false
						this.completeLoading()
					}, 10000)
					const percentComplete = (xhr.loaded / xhr.total)
					const red = Math.ceil(255 - (percentComplete * 255))
					const green = Math.ceil(255 - (percentComplete * 8))
					const blue = Math.ceil(255 - (percentComplete * 0))
					this.loadingObject.material.color = new THREE.Color(`rgb(${red}, ${green}, ${blue})`)
				}
			},
			(err) => {
				showMessage(`${t('Failed to load')} ${this.title}`)
				console.error(err)
				this.loading = false
				this.completeLoading()
			}
		)
	}

	startLoading(position) {
		const loadingGeometry = new THREE.CylinderBufferGeometry(15, 25, 140, 15)
		const loadingMaterial = new THREE.MeshBasicMaterial( { color: new THREE.Color('rgb(255,255,255') } );
		this.loadingObject = new THREE.Mesh(loadingGeometry, loadingMaterial)
		scene.add(this.loadingObject)
		const headGeometry = new THREE.SphereBufferGeometry(15, 15)
		const headObject = new THREE.Mesh(headGeometry, loadingMaterial)
		this.loadingObject.add(headObject)
		this.loadingObject.visible = false
		setTimeout(() => {
			this.loadingObject.visible = true
			headObject.position.set(0, 90, 0)
			this.loadingObject.position.copy(position || this.getSpawnPoint(this.subjectType))
			this.loadingObject.scale.set(0.9,0.9,0.9)
			
			let direction = 1
			const animate = (time) => {
				if (this.loadingObject) {
					requestAnimationFrame(animate)
					if (direction == 1 && this.loadingObject.scale.x < 1) {
							this.loadingObject.scale.x += 0.001
							this.loadingObject.scale.y += 0.001
							this.loadingObject.scale.z += 0.001
						if (this.loadingObject.scale.x >= 1) {
							direction = 0
						}
					} else {
						this.loadingObject.scale.x -= 0.001
						this.loadingObject.scale.y -= 0.001
						this.loadingObject.scale.z -= 0.001
					
						if (this.loadingObject.scale.x <= 0.95) {
							direction = 1
						}
					}
				}
			}
			requestAnimationFrame(animate)
		}, 50)
	}

	completeLoading() {
		if (this.loadingObject) {
			this.loadingObject.visible = false
			setTimeout(() => {
				scene.remove(this.loadingObject)
				this.loadingObject = null
			}, 100)
		}
	}

	get base() {
		return this.hip
	}

	traverseJoints(action) {
		const jointChecker = []
		const checked = []
		if (!this.object) return
		this.object.traverse((child) => {
			if (child.isObject3D) {
				!jointChecker.includes(child.name) &&
					jointChecker.push(`"${child.name}"`)

				if (
					this.info.joints.some((joint) => joint.name === child.name) &&
					!checked.includes(child.name)
				) {
					action(child)
					checked.push(child.name)
				}
			}
		})
	}

	getJoint(name) {
		const joint = this.object.getObjectByName(name)
		return joint
	}

	setMaterial() {
		if (!this.color) return
		this.object.receiveShadow = true
		this.object.castShadow = true
		this.object.traverse((child) => {
			if (child.isMesh && child.type == "SkinnedMesh") {
				child.castShadow = true
				child.receiveShadow = true
				child.frustumCulled = false
				child.material = new THREE.MeshPhongMaterial({
					color: new THREE.Color(this.color),
					skinning: true,
				})
			}
		})
	}

	getBoundingObject() {
		if (
			this.object.children[0].type == "Bone" ||
			this.object.children[0].type == "Group"
		) {
			return this.object.children[0]
		}
		if (
			this.object.children[1].type == "Bone" ||
			this.object.children[1].type == "Group"
		) {
			return this.object.children[1]
		}
		if (
			this.object.children[2].type == "Bone" ||
			this.object.children[2].type == "Group"
		) {
			return this.object.children[2]
		}
	}

	setJointsVisibility(hidden = false) {
		this.joints.forEach((joint) => {
			joint.visible =
				this.alive &&
				!this.jointsHidden &&
				!hidden &&
				(sceneManager.activeGizmo === "rotate" ||
				(sceneManager.activeGizmo === "scale" && options.jointScaling)) &&
				joint.type != "finger"
			joint.reset()
		})
		if (sceneManager.activeGizmo != "hidden" && !hidden) {
			this.joints[0].visible = this.alive && !this.jointsHidden
		}
	}

	hover() {
		this.object.children.forEach((child) => {
			if (child.material) {
				child.material.opacity = 0.5
			}
		})
	}

	unhover() {
		this.object.children.forEach((child) => {
			if (child.material) {
				child.material.opacity = 1
			}
		})
	}

	deselect() {
		let shouldDetach = false
		this.joints.forEach((joint) => {
			if (joint.parent == sceneManager.gizmo.object) {
				shouldDetach = true
			}
			joint.reset()
		})
		if (shouldDetach) {
			sceneManager.gizmo.detach()
		}
	}

	selectCharacter() {
		sceneManager.selectedManager.select(this)
		if (this.jointsHidden || !this.object.visible) {
			sceneManager.gizmo.detach()
			return
		}
		this.selectJoint(this.joints[0])
	}


	enterHandEditor(side) {
		this.editingHand = true
		this.setOverlayBox(side)
	
		sceneManager.sceneCharacters.forEach((character) => {
			character.setJointsVisibility(true)
		})
	
		this.originalTarget = new THREE.Vector3(
			orbitControl.target.x,
			orbitControl.target.y,
			orbitControl.target.z
		)
		this.originalCameraPosition = new THREE.Vector3(
			camera.position.x,
			camera.position.y,
			camera.position.z
		)
	
		const mid = this.joints.filter(
			(joint) => joint.name.indexOf(`${side}HandMiddle1`) > 0
		)[0]

		const newFocus = new THREE.Vector3()
		mid.getWorldPosition(newFocus)
	
		animateTransition({value: 1000}, {value: 40}, tween => {
			camera.position.sub(newFocus).setLength(tween.value).add(newFocus)
		})

		orbitControl.target.set(newFocus.x, newFocus.y, newFocus.z)
		orbitControl.update()
		this.joints.forEach((joint) => {
			if (joint.type == "finger" && joint.name.indexOf(side) > 0) {
				joint.visible = true
			} else {
				joint.visible = false
			}
		})
	
		const otherHand = side == "Left" ? "Right" : "Left"
		this.joints.filter((joint) => joint.name == otherHand)[0].visible =
			!this.editingHand
	}
	
	exitHandEditor() {
		this.deselect()
		this.editingHand = false
		this.container.remove()
	
		sceneManager.sceneCharacters.forEach((character) => {
			character.setJointsVisibility()
		})

		camera.position.set(
			this.originalCameraPosition.x,
			this.originalCameraPosition.y,
			this.originalCameraPosition.z
		)
	
		orbitControl.target.set(
			this.originalTarget.x,
			this.originalTarget.y,
			this.originalTarget.z
		)
		orbitControl.update()
	}
	
	editHand(side) {
		if (!this.editingHand) {
			this.enterHandEditor(side)
		} else {
			this.exitHandEditor()
		}
	}
	
	setOverlayBox(side) {
		const body = document.querySelector("body")

		this.container = document.createElement("div")
		this.container.classList.add("hand-editor")
		this.container.style.display = "block"
		body.prepend(this.container)

		const top = document.createElement("div")
		top.classList.add("overlay", "top")
		top.style.height = "60px"
		top.style.left = "60px"
		top.style.right = "60px"
		const bottom = document.createElement("div")
		bottom.classList.add("overlay", "bottom")
		bottom.style.height = "60px"
		bottom.style.left = "60px"
		bottom.style.right = "60px"
		const left = document.createElement("div")
		left.classList.add("overlay", "left")
		left.style.width = "60px"
		const right = document.createElement("div")
		right.classList.add("overlay", "right")
		right.style.width = "60px"

		const closeButton = document.createElement("button")
		closeButton.classList.add("button", "small", "mint", "close")
		closeButton.innerText = t("Exit hand editor")
		closeButton.onclick = () => this.editHand(side)

		this.container.append(closeButton, top, bottom, left, right)
	}

	selectJoint(selectedJoint) {
		if (selectedJoint.type == "selector") {
			this.editHand(selectedJoint.name)
		} else {
			sceneManager.gizmo.updateObject(selectedJoint.parent)

			if (sceneManager.activeGizmo === "rotate") {
				this.joints.forEach((joint) => {
					joint.reset()
				})

				selectedJoint.setColor(jointColors.SELECTED_COLOR)
				selectedJoint.selected = true
			}
		}
	}

	toggleVisibility() {
		if (this.object.visible) {
			this.object.visible = false;
			if (!this.jointsHidden) {
				this.setJointsVisibility()
				sceneManager.gizmo.detach()
			}
		} else {
			this.object.visible = true;
			if (!this.jointsHidden) {
				this.setJointsVisibility()
			}
		}
	}

	toggleJoints() {
		if (this.jointsHidden) {
			this.showJoints()
		} else {
			this.hideJoints()
		}
	}

	hideJoints() {
		this.jointsHidden = true
		this.setJointsVisibility()
		sceneManager.gizmo.detach()
	}

	showJoints() {
		this.jointsHidden = false
		this.setJointsVisibility()
	}

	setGizmo() {
		if (this.joints.length > 0) {
			if (
				sceneManager.selectedManager.subject == this &&
				sceneManager.gizmo.isAttached
			) {
				sceneManager.gizmo.updateObject(this.hip)
			} else {
				this.deselect()
			}
			this.setJointsVisibility()
		}
	}

	checkIntersects(raycaster) {
		const visibleJoints = this.joints.filter((joint) => joint.visible)
		const intersects = raycaster.intersectObjects(visibleJoints)
		if (intersects.length > 0) {
			const closest = intersects
				.sort((a, b) => a.distance - b.distance)
				.filter((item, index, array) => item.distance === array[0].distance)[0]

			return {
				object: this,
				distance: closest.distance,
				itemClicked: closest.object,
			}
		}
	}

	onTap(joint) {
		if(sceneManager.selectedManager.propToConnect) {
			const prop = sceneManager.selectedManager.propToConnect
			if (prop) {
					
					showConfirm(t('Move to joint?'), () => {
						prop.attachToJoint(this, joint.parent, true)
						showMessage(`${prop.title || t("Prop")} ${t('is now attached to')} ${joint.name.replace("mixamorig", "")}`, 5000, 100)
					}, () => {
						prop.attachToJoint(this, joint.parent)
						showMessage(`${prop.title || t("Prop")} ${t('is now attached to')} ${joint.name.replace("mixamorig", "")}`, 5000, 100)
					})
			}
		} else {
			sceneManager.selectedManager.select(this)
			this.selectJoint(joint)
		}
	}

	onSecondaryTap(joint) {

	}

	getPose() {
		let pose = {}
		this.traverseJoints((joint) => {
			pose[joint.name] = {
				x: joint.rotation.x,
				y: joint.rotation.y,
				z: joint.rotation.z,
			}
		})
		return pose
	}

	setPose(pose, animate=true, store=false) {
		if (!pose) return

		if (store) {
			sceneManager.stateManager.storeSceneState();
		}

		this.traverseJoints((joint) => {
			if (pose[joint.name]) {
				if (animate) {
					animateTransition(
						{
							x: joint.rotation.x,
							y: joint.rotation.y,
							z: joint.rotation.z,
						},
						{
							x: pose[joint.name].x,
							y: pose[joint.name].y,
							z: pose[joint.name].z,
						},
						tween => joint.rotation.set(tween.x, tween.y, tween.z)
					)
				} else {
					joint.rotation.set(pose[joint.name].x, pose[joint.name].y, pose[joint.name].z)
				}

				quickSave()
			}
		})
	}

	getJointScales() {
		let jointScales = {}
		this.traverseJoints((joint) => {
			jointScales[joint.name] = {
				x: joint.scale.x,
				y: joint.scale.y,
				z: joint.scale.z,
			}
		})
		return jointScales
	}

	setJointScales(jointScales, animate=true, store=false) {
		if (!jointScales) return

		this.traverseJoints((joint) => {
			if (jointScales[joint.name]) {
				if (animate) {
					animateTransition(
						{
							x: joint.scale.x,
							y: joint.scale.y,
							z: joint.scale.z,
						},
						{
							x: jointScales[joint.name].x,
							y: jointScales[joint.name].y,
							z: jointScales[joint.name].z,
						},
						tween => joint.scale.set(tween.x, tween.y, tween.z)
					)
				} else {
					joint.scale.set(jointScales[joint.name].x, jointScales[joint.name].y, jointScales[joint.name].z)
				}

				quickSave()
			}
		})
	}

	flip() {
		const currentPose = this.getPose()
		let newPose = {}
		Object.entries(currentPose).forEach((joint) => {
			if (joint[0].includes("Left")) {
				newPose[joint[0].replace("Left", "Right")] = {
					x: joint[1].x,
					y: -joint[1].y,
					z: -joint[1].z,
				}
			} else if (joint[0].includes("Right")) {
				newPose[joint[0].replace("Right", "Left")] = {
					x: joint[1].x,
					y: -joint[1].y,
					z: -joint[1].z,
				}
			} else {
				newPose[joint[0]] = {
					x: joint[1].x,
					y: -joint[1].y,
					z: -joint[1].z,
				}
			}
		})
		this.setPose(newPose)
		return newPose
	}

	delete() {
		sceneManager.gizmo.detach()
		removeFromTabs(this)
		sceneManager.stateManager.storeSceneState()

		this.object.traverse(function (child) {
			if (child instanceof THREE.Mesh) {
				child.visible = false
			}
		})

		this.alive = false

		const attachedProps = sceneManager.sceneProps.filter(prop => prop.attachedTo && prop.attachedTo.modelId == this.id)
		attachedProps.forEach(prop => prop.delete())

		quickSave()
	}

	reinstate() {
		addCharacterToTabs(this)
		this.unhover()
		this.alive = true
		this.object.traverse(function (child) {
			if (child instanceof THREE.Mesh) {
				child.visible = true
			}
		})
		this.setJointsVisibility()
	}

	duplicate() {
		const newPosition = {
			x: this.getPosition().x + 60,
			y: this.getPosition().y,
			z: this.getPosition().z,
		}
		const newCharacter = sceneManager.addCharacterToScene({
			info: this.info,
			position: newPosition,
			scale: this.getScale(),
			pose: this.getPose(),
			color: this.color
		})

		const attachedProps = sceneManager.sceneProps.filter(prop => prop.attachedTo && prop.attachedTo.modelId == this.id)
		
		attachedProps.forEach(prop => {
			sceneManager.addPropToScene({
				type: prop.type,
				position: prop.getPosition(),
				scale: prop.getScale(),
				rotation: prop.getRotation(),
				imageUrl: prop.imageUrl,
				objectInfo: prop.objectInfo,
				title: prop.title,
				color: prop.color,
				icon: prop.icon,
				attachedTo: {modelId: newCharacter.id, jointName: prop.attachedTo.jointName}
			})
		})
		
	}

	putOnGround(animate = true) {
		if (this.info.centeredOnly) return
		const oldPosition = this.getPosition()

		sceneManager.stateManager.storeSceneState()

		const box = new THREE.Box3().setFromObject(this.getBoundingObject())
		const newYPosition = oldPosition.y - box.min.y
		const groundedPosition = new THREE.Vector3(
			oldPosition.x,
			newYPosition,
			oldPosition.z
		)
		this.setPosition(groundedPosition, animate)

		quickSave()
	}

	mirrorRotation(joint) {
		if (joint.name.includes('Left') || joint.name.includes('Right')){
			const mirroredJointName = joint.name.includes('Left')
				? joint.name.replace('Left', 'Right')
				: joint.name.replace('Right', 'Left');
		
			const mirroredJoint = this.getJoint(mirroredJointName);
		
			if (mirroredJoint) {
				mirroredJoint.rotation.set(
					joint.rotation.x,
					-joint.rotation.y,
					-joint.rotation.z
				);
			}
		}
	}

	mirrorScale(joint) {
		if (joint.name.includes('Left') || joint.name.includes('Right')){
			const mirroredJointName = joint.name.includes('Left')
				? joint.name.replace('Left', 'Right')
				: joint.name.replace('Right', 'Left');
		
			const mirroredJoint = this.getJoint(mirroredJointName);
		
			if (mirroredJoint) {
				mirroredJoint.scale.set(
					joint.scale.x,
					joint.scale.y,
					joint.scale.z
				);
			}
		}
	}



	prepareExport(format) {
		/*  Apply bone rotations to a model */

		let exportGroup = this.object.clone(true)
		let objectsToRemove = []

		for (let i = 0; i < exportGroup.children.length; i++) {
			let child = exportGroup.children[i]
			let skeleton = this.object.children[i].skeleton

			if (child.isSkinnedMesh && skeleton) {
				skeleton = skeleton.clone()
				child.bind(skeleton, this.object.children[i].bindMatrix)

				for (let bone of skeleton.bones) {
					bone.updateMatrixWorld(true)
				}

				skeleton.update()

				child.geometry = this.object.children[i].geometry.clone()

				const position = new THREE.Vector3()
				const transformed = new THREE.Vector3()
				const temp1 = new THREE.Vector3()
				const tempBoneMatrix = new THREE.Matrix4()
				const tempSkinnedVertex = new THREE.Vector3()
				const tempSkinned = new THREE.Vector3()

				const bindMatrix = child.bindMatrix
				const bindMatrixInverse = child.bindMatrixInverse

				if (child.geometry.isBufferGeometry) {
					for (
						let vertexIndex = 0;
						vertexIndex < child.geometry.attributes.position.count;
						vertexIndex++
					) {
						position.fromBufferAttribute(
							child.geometry.attributes.position,
							vertexIndex
						)
						transformed.copy(position)

						if (child.geometry.morphAttributes.position) {
							for (
								let i = 0;
								i < child.geometry.morphAttributes.position.length;
								i++
							) {
								temp1.copy(
									new THREE.Vector3().fromBufferAttribute(
										child.geometry.morphAttributes.position[i],
										vertexIndex
									)
								)
								transformed.add(
									temp1
										.sub(position)
										.multiplyScalar(child.morphTargetInfluences[i])
								)
							}
						}

						tempSkinnedVertex.copy(transformed).applyMatrix4(bindMatrix)
						tempSkinned.set(0, 0, 0)

						const boneIndex = new THREE.Vector4()
						boneIndex.fromBufferAttribute(
							child.geometry.attributes.skinIndex,
							vertexIndex
						)
						const weight = new THREE.Vector4()
						weight.fromBufferAttribute(
							child.geometry.attributes.skinWeight,
							vertexIndex
						)

						for (let i = 0; i < 4; i++) {
							tempBoneMatrix.fromArray(
								skeleton.boneMatrices,
								boneIndex.getComponent(i) * 16
							)
							temp1.copy(tempSkinnedVertex)
							tempSkinned.add(
								temp1
									.applyMatrix4(tempBoneMatrix)
									.multiplyScalar(weight.getComponent(i))
							)
						}

						transformed.copy(tempSkinned).applyMatrix4(bindMatrixInverse)
						transformed.applyMatrix4(child.matrixWorld)

						child.geometry.attributes.position.setXYZ(
							vertexIndex,
							transformed.x,
							transformed.y,
							transformed.z
						)
					}
				} else if (child.geometry.isGeometry) {
					for (
						let vertexIndex = 0;
						vertexIndex < child.geometry.vertices.length;
						vertexIndex++
					) {
						position.copy(child.geometry.vertices[vertexIndex])
						transformed.copy(position)

						for (let i = 0; i < child.geometry.morphTargets.length; i++) {
							temp1.copy(child.geometry.morphTargets[i].vertices[vertexIndex])
							transformed.add(
								temp1
									.sub(position)
									.multiplyScalar(child.morphTargetInfluences[i])
							)
						}

						tempSkinnedVertex.copy(transformed).applyMatrix4(bindMatrix)
						tempSkinned.set(0, 0, 0)

						const skinIndices = child.geometry.skinIndices[vertexIndex]
						const skinWeights = child.geometry.skinWeights[vertexIndex]

						for (let i = 0; i < 4; i++) {
							const boneNdx = skinIndices.getComponent(i)
							const weight = skinWeights.getComponent(i)
							tempBoneMatrix.fromArray(skeleton.boneMatrices, boneNdx * 16)
							temp1.copy(tempSkinnedVertex)
							tempSkinned.add(
								temp1.applyMatrix4(tempBoneMatrix).multiplyScalar(weight)
							)
						}

						transformed.copy(tempSkinned).applyMatrix4(bindMatrixInverse)
						transformed.applyMatrix4(child.matrixWorld)
					}
				}
			} else if (!child.isSkinnedMesh) {
				objectsToRemove.push(child)
			}
		}

		for (let object of objectsToRemove) {
			object.parent.remove(object)
		}

		const modelExporter = new ModelExporter()
		if (format == "dae") {
			modelExporter.exportCollada(exportGroup)
		} else if (format == "obj") {
			modelExporter.exportObj(exportGroup)
		}
	}
}

export default Character
